import { MitgoId } from '@common-stream/types';
import { App, Button, Input, Radio, Select, Space, Typography } from 'antd';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ValidationError } from 'yup';
import * as yup from 'yup';

import { getPassportRegistrationSurveyUrl, REGISTRATION_SURVEY_REQUEST_TIMEOUT_MS } from '../../constants';
import AuthContext from '../../context/AuthContext';
import { sendRequest } from '../../requests/sendRequest';
import { applyCmpimportToRedirectUrl } from '../../utils/cmpimportHash';
import { resolveExitUrlFromRedirectUriParamWithLiteAdmitadGate } from '../../utils/surveyRedirectTarget';
import { transformYupErrorsIntoObject } from '../../utils/transformYupErrorsIntoObject';

import CURRENCY_RATES from './currencyRate.json';
import { CURRENCY_OPTIONS, CURRENCY_SYMBOLS } from './RegistrationSurvey.constants';
import styles from './RegistrationSurvey.module.scss';
import { getDefaultCurrencyByDeviceCountry } from './RegistrationSurvey.utils';

const {
  RegistrationSurveySource,
  RegistrationSurveyWorkType,
  RegistrationSurveyTeamSize,
  RegistrationSurveyExperience,
  RegistrationSurveyMonthlyRevenue,
} = MitgoId.Shared;

const SourceOptions = [
  MitgoId.Shared.RegistrationSurveySource.SearchEngine,
  MitgoId.Shared.RegistrationSurveySource.Advertisement,
  MitgoId.Shared.RegistrationSurveySource.Podcast,
  MitgoId.Shared.RegistrationSurveySource.EventOrConference,
  MitgoId.Shared.RegistrationSurveySource.SocialMedia,
  MitgoId.Shared.RegistrationSurveySource.BlogOrPublication,
  MitgoId.Shared.RegistrationSurveySource.OnlineCommunity,
  MitgoId.Shared.RegistrationSurveySource.WordOfMouth,
  MitgoId.Shared.RegistrationSurveySource.Other,
] as const;

type CurrencyRatesMap = Record<string, number>;

const currencyRatesTyped: CurrencyRatesMap = CURRENCY_RATES as CurrencyRatesMap;

/**
 * Пересчитывает отображаемую сумму для вариантов выручки (аналог passport-client).
 *
 * @param {number} baseAmount — базовая сумма в USD
 * @param {number} rate — курс валюты к USD
 * @returns {number}
 */
function rescaleCurrencyAmount(baseAmount: number, rate: number): number {
  const magnitude = Math.pow(10, Math.ceil(Math.log10(Math.ceil(rate))));
  let result = baseAmount * magnitude;
  if (result > 10_000_000) {
    result = 10_000_000;
  }
  return result;
}

/**
 * Форматирует сумму в заданной валюте.
 *
 * @param {number} amount — значение
 * @param {string} currencyCode — ISO код валюты
 * @returns {string}
 */
function formatCurrencyAmount(amount: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currencyCode}`;
  }
}

/**
 * Возвращает курс USD→currency из таблицы или 1.
 *
 * @param {string} currencyCode — код валюты
 * @param {CurrencyRatesMap} rates — таблица курсов
 * @returns {number}
 */
function lookupCurrencyRate(currencyCode: string, rates: CurrencyRatesMap): number {
  if (Object.hasOwn(rates, currencyCode)) {
    const v = rates[currencyCode];
    if (typeof v === 'number') {
      return v;
    }
  }
  const usd = rates.USD;
  if (typeof usd === 'number') {
    return usd;
  }
  return 1;
}

/**
 * Данные формы регистрационного опроса (без серверных полей).
 */
type SurveyFormData = {
  source: (typeof SourceOptions)[number] | null;
  sourceOther: string | null;
  workType: MitgoId.Shared.RegistrationSurveyWorkType | null;
  teamSize: MitgoId.Shared.RegistrationSurveyTeamSize | null;
  experience: MitgoId.Shared.RegistrationSurveyExperience | null;
  hasFirstUsers: boolean | null;
  currencyCode: string;
  monthlyRevenue: MitgoId.Shared.RegistrationSurveyMonthlyRevenue | null;
};

/**
 * Собирает схему Yup для опроса.
 *
 * @param {TFunction} t — функция перевода
 * @returns {ReturnType<typeof yup.object>} схема валидации полей формы
 */
function getFormValidationScheme(t: TFunction) {
  return yup.object().shape({
    source: yup.string().required(t('commonErrors.required')),
    sourceOther: yup.string().when('source', {
      is: RegistrationSurveySource.Other,
      // eslint-disable-next-line unicorn/no-thenable
      then: (schema) => schema.required(t('commonErrors.required')),
      otherwise: (schema) => schema.nullable(),
    }),
    workType: yup.string().required(t('commonErrors.required')),
    teamSize: yup.string().required(t('commonErrors.required')),
    experience: yup.string().required(t('commonErrors.required')),
    hasFirstUsers: yup.boolean().required(t('commonErrors.required')),
    currencyCode: yup.string().required(t('commonErrors.required')),
    monthlyRevenue: yup.string().required(t('commonErrors.required')),
  });
}

/**
 * Возвращает текст ошибки поля из плоского объекта Yup.
 *
 * @param {Record<string, string>} errors — ошибки по путям
 * @param {string} key — ключ поля
 * @returns {string | null}
 */
function fieldError(errors: Record<string, string>, key: string): string | null {
  if (!Object.hasOwn(errors, key)) {
    return null;
  }
  const msg = errors[key];
  if (msg === undefined || msg === '') {
    return null;
  }
  return msg;
}

/**
 * Страница регистрационного опроса (Ant Design + yup), POST на Passport API.
 *
 * @returns {ReactElement}
 */
export function RegistrationSurvey(): ReactElement {
  const { t } = useTranslation();
  const { kToken } = useContext(AuthContext);
  const { message } = App.useApp();
  const location = useLocation();

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState<SurveyFormData>(() => ({
    source: null,
    sourceOther: null,
    workType: null,
    teamSize: null,
    experience: null,
    hasFirstUsers: null,
    currencyCode: getDefaultCurrencyByDeviceCountry(),
    monthlyRevenue: null,
  }));

  // Состояние запроса на создание опроса
  const [surveyPending, setSurveyPending] = useState(false);
  const [surveyCreated, setSurveyCreated] = useState(false);
  const [surveyCreateErrored, setSurveyCreateErrored] = useState(false);

  // Memo функция для схемы валидации формы
  const validationScheme = useMemo(() => getFormValidationScheme(t), [t]);

  // Memo функция для опций выбора источника информации
  const sourceSelectOptions = useMemo(
    () =>
      SourceOptions.map((value) => ({
        value,
        label: t(`page.registrationSurvey.options.source.${value}`),
      })),
    [t],
  );

  const rate = useMemo(() => lookupCurrencyRate(form.currencyCode, currencyRatesTyped), [form.currencyCode]);

  // Memo функция для опций месячной выручки для радио кнопок
  const revenueRadioOptions = useMemo(() => {
    const code = form.currencyCode;
    return [
      {
        value: RegistrationSurveyMonthlyRevenue.PreRevenue,
        label: t('page.registrationSurvey.options.monthlyRevenue.PreRevenue'),
      },
      {
        value: RegistrationSurveyMonthlyRevenue.UpTo1000,
        label: t('page.registrationSurvey.options.monthlyRevenue.UpTo1000', {
          first: formatCurrencyAmount(rescaleCurrencyAmount(1, rate), code),
          second: formatCurrencyAmount(rescaleCurrencyAmount(1000, rate), code),
        }),
      },
      {
        value: RegistrationSurveyMonthlyRevenue.UpTo10000,
        label: t('page.registrationSurvey.options.monthlyRevenue.UpTo10000', {
          first: formatCurrencyAmount(rescaleCurrencyAmount(1000, rate), code),
          second: formatCurrencyAmount(rescaleCurrencyAmount(10_000, rate), code),
        }),
      },
      {
        value: RegistrationSurveyMonthlyRevenue.UpTo100000,
        label: t('page.registrationSurvey.options.monthlyRevenue.UpTo100000', {
          first: formatCurrencyAmount(rescaleCurrencyAmount(10_000, rate), code),
        }),
      },
      {
        value: RegistrationSurveyMonthlyRevenue.MoreThan100000,
        label: t('page.registrationSurvey.options.monthlyRevenue.MoreThan100000', {
          first: formatCurrencyAmount(rescaleCurrencyAmount(100_000, rate), code),
        }),
      },
    ];
  }, [form.currencyCode, rate, t]);

  // Memo функция для проверки, должна ли кнопка "Далее" быть отключена
  const isNextButtonDisabled = useMemo(() => {
    if (form.source === null) {
      return true;
    }
    if (form.source === RegistrationSurveySource.Other && (form.sourceOther === null || form.sourceOther === '')) {
      return true;
    }
    if (form.workType === null) {
      return true;
    }
    if (form.teamSize === null) {
      return true;
    }
    if (form.experience === null) {
      return true;
    }
    if (form.hasFirstUsers === null) {
      return true;
    }
    if (form.monthlyRevenue === null) {
      return true;
    }
    if (form.currencyCode === '') {
      return true;
    }
    return false;
  }, [form]);

  const validateForm = useCallback(() => {
    try {
      validationScheme.validateSync(form, {
        abortEarly: false,
      });
      return { isValid: true as const, errors: {} as Record<string, string> };
    } catch (error: unknown) {
      if (error instanceof ValidationError) {
        return { isValid: false as const, errors: transformYupErrorsIntoObject(error) };
      }
      throw error;
    }
  }, [form, validationScheme]);

  const handleSubmit = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    if (typeof kToken !== 'string' || kToken === '') {
      message.error(t('commonErrors.somethingWentWrong'));
      return;
    }

    setSurveyPending(true);
    setSurveyCreated(false);
    setSurveyCreateErrored(false);
    // До await: иначе после сетевой задержки в `window`/`router` query мог уже отличаться (гонка с гвардом и т.п.).
    const exitRedirectUri = new URLSearchParams(location.search).get('redirect_uri');
    try {
      await sendRequest({
        method: 'POST',
        url: getPassportRegistrationSurveyUrl(),
        data: form,
        headers: {
          Authorization: `Bearer ${kToken}`,
          'Content-Type': 'application/json',
        },
        timeoutMs: REGISTRATION_SURVEY_REQUEST_TIMEOUT_MS,
      });

      setSurveyCreated(true);

      // Business override: если выполнены условия Q3/Q4, игнорируем исходный redirect_uri
      // и отправляем пользователя в Admitad Lite, передавая origin_redirect_uri как callback.
      const allowLiteAdmitadRedirect =
        form.teamSize === RegistrationSurveyTeamSize.Solo &&
        (form.experience === RegistrationSurveyExperience.JustStarting ||
          form.experience === RegistrationSurveyExperience.MoreThanYear);

      if (allowLiteAdmitadRedirect) {
        const liteUrl = new URL('https://lite.admitad.com/');
        if (typeof exitRedirectUri === 'string' && exitRedirectUri !== '') {
          try {
            const origin = new URL(exitRedirectUri);
            if (origin.protocol === 'http:' || origin.protocol === 'https:') {
              liteUrl.searchParams.set('origin_redirect_uri', exitRedirectUri);
            }
          } catch {
            // ignore invalid origin redirect_uri
          }
        }

        window.location.assign(applyCmpimportToRedirectUrl(liteUrl.toString()));
        return;
      }

      window.location.assign(resolveExitUrlFromRedirectUriParamWithLiteAdmitadGate(exitRedirectUri, false));
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : t('commonErrors.somethingWentWrong');
      message.error(errMsg);
      setSurveyCreateErrored(true);
    } finally {
      setSurveyPending(false);
    }
  }, [validateForm, form, kToken, message, t, location.search]);

  const clearError = useCallback((key: string) => {
    setFormErrors((prev) => {
      if (!Object.hasOwn(prev, key)) {
        return prev;
      }
      return { ...prev, [key]: '' };
    });
  }, []);

  return (
    <main className={styles.page}>
      <div className={styles.content}>
        <section className={styles.card}>
          <div className={styles.inner}>
            <div className={styles.head}>
              <Typography.Title level={4} className={styles.title}>
                {t('page.registrationSurvey.title')}
              </Typography.Title>
            </div>

            <div className={styles.question}>
              <Typography.Text strong className={styles.questionTitle}>
                1. {t('page.registrationSurvey.questions.source')}
              </Typography.Text>
              <Select
                allowClear
                placeholder={t('page.registrationSurvey.placeholders.source')}
                value={form.source === null ? undefined : form.source}
                options={sourceSelectOptions}
                onChange={(value) => {
                  const next = value === undefined ? null : value;
                  setForm((prev) => ({
                    ...prev,
                    source: next,
                    sourceOther: next === RegistrationSurveySource.Other ? prev.sourceOther : null,
                  }));
                  clearError('source');
                  clearError('sourceOther');
                }}
                className={styles.selectFullWidth}
              />
              {fieldError(formErrors, 'source') !== null ? (
                <p className={styles.fieldError}>{fieldError(formErrors, 'source')}</p>
              ) : null}

              {form.source === RegistrationSurveySource.Other ? (
                <>
                  <Input
                    placeholder={t('page.registrationSurvey.placeholders.sourceOther')}
                    value={form.sourceOther === null ? '' : form.sourceOther}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((prev) => ({ ...prev, sourceOther: v === '' ? null : v }));
                      clearError('sourceOther');
                    }}
                  />
                  {fieldError(formErrors, 'sourceOther') !== null ? (
                    <p className={styles.fieldError}>{fieldError(formErrors, 'sourceOther')}</p>
                  ) : null}
                </>
              ) : null}
            </div>

            <div className={styles.question}>
              <Typography.Text strong className={styles.questionTitle}>
                2. {t('page.registrationSurvey.questions.workType')}
              </Typography.Text>
              <Radio.Group
                value={form.workType === null ? undefined : form.workType}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({ ...prev, workType: v }));
                  clearError('workType');
                }}
              >
                <Space direction='vertical'>
                  <Radio value={RegistrationSurveyWorkType.FullTime}>
                    {t('page.registrationSurvey.options.workType.FullTime')}
                  </Radio>
                  <Radio value={RegistrationSurveyWorkType.PartTime}>
                    {t('page.registrationSurvey.options.workType.PartTime')}
                  </Radio>
                </Space>
              </Radio.Group>
              {fieldError(formErrors, 'workType') !== null ? (
                <p className={styles.fieldError}>{fieldError(formErrors, 'workType')}</p>
              ) : null}
            </div>

            <div className={styles.question}>
              <Typography.Text strong className={styles.questionTitle}>
                3. {t('page.registrationSurvey.questions.teamSize')}
              </Typography.Text>
              <Radio.Group
                value={form.teamSize === null ? undefined : form.teamSize}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, teamSize: e.target.value }));
                  clearError('teamSize');
                }}
              >
                <Space direction='vertical'>
                  <Radio value={RegistrationSurveyTeamSize.Solo}>
                    {t('page.registrationSurvey.options.teamSize.Solo')}
                  </Radio>
                  <Radio value={RegistrationSurveyTeamSize.UpTo10}>
                    {t('page.registrationSurvey.options.teamSize.UpTo10')}
                  </Radio>
                  <Radio value={RegistrationSurveyTeamSize.MoreThan10}>
                    {t('page.registrationSurvey.options.teamSize.MoreThan10')}
                  </Radio>
                </Space>
              </Radio.Group>
              {fieldError(formErrors, 'teamSize') !== null ? (
                <p className={styles.fieldError}>{fieldError(formErrors, 'teamSize')}</p>
              ) : null}
            </div>

            <div className={styles.question}>
              <Typography.Text strong className={styles.questionTitle}>
                4. {t('page.registrationSurvey.questions.experience')}
              </Typography.Text>
              <Radio.Group
                value={form.experience === null ? undefined : form.experience}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, experience: e.target.value }));
                  clearError('experience');
                }}
              >
                <Space direction='vertical'>
                  <Radio value={RegistrationSurveyExperience.JustStarting}>
                    {t('page.registrationSurvey.options.experience.JustStarting')}
                  </Radio>
                  <Radio value={RegistrationSurveyExperience.MoreThanYear}>
                    {t('page.registrationSurvey.options.experience.MoreThanYear')}
                  </Radio>
                  <Radio value={RegistrationSurveyExperience.MoreThan5Years}>
                    {t('page.registrationSurvey.options.experience.MoreThan5Years')}
                  </Radio>
                </Space>
              </Radio.Group>
              {fieldError(formErrors, 'experience') !== null ? (
                <p className={styles.fieldError}>{fieldError(formErrors, 'experience')}</p>
              ) : null}
            </div>

            <div className={styles.question}>
              <Typography.Text strong className={styles.questionTitle}>
                5. {t('page.registrationSurvey.questions.hasFirstUsers')}
              </Typography.Text>
              <Radio.Group
                value={form.hasFirstUsers === null ? undefined : form.hasFirstUsers ? 'true' : 'false'}
                onChange={(e) => {
                  const raw = e.target.value;
                  const next = raw === 'true' ? true : raw === 'false' ? false : null;
                  setForm((prev) => ({ ...prev, hasFirstUsers: next }));
                  clearError('hasFirstUsers');
                }}
              >
                <Space direction='vertical'>
                  <Radio value='true'>{t('page.registrationSurvey.options.hasFirstUsers.true')}</Radio>
                  <Radio value='false'>{t('page.registrationSurvey.options.hasFirstUsers.false')}</Radio>
                </Space>
              </Radio.Group>
              {fieldError(formErrors, 'hasFirstUsers') !== null ? (
                <p className={styles.fieldError}>{fieldError(formErrors, 'hasFirstUsers')}</p>
              ) : null}
            </div>

            <div className={styles.question}>
              <Typography.Text strong className={styles.questionTitle}>
                6. {t('page.registrationSurvey.questions.currency')}
              </Typography.Text>
              <Select
                placeholder={t('page.registrationSurvey.placeholders.currency')}
                value={form.currencyCode === '' ? undefined : form.currencyCode}
                options={CURRENCY_OPTIONS.map((currencyCode) => ({
                  value: currencyCode,
                  label: `${currencyCode}, ${CURRENCY_SYMBOLS[currencyCode]}`,
                }))}
                onChange={(value) => {
                  const code = value === undefined ? getDefaultCurrencyByDeviceCountry() : value;
                  setForm((prev) => ({ ...prev, currencyCode: code }));
                  clearError('currencyCode');
                }}
                className={styles.selectFullWidth}
              />
              {fieldError(formErrors, 'currencyCode') !== null ? (
                <p className={styles.fieldError}>{fieldError(formErrors, 'currencyCode')}</p>
              ) : null}
            </div>

            <div className={styles.question}>
              <Typography.Text strong className={styles.questionTitle}>
                7. {t('page.registrationSurvey.questions.monthlyRevenue')}
              </Typography.Text>
              <Radio.Group
                value={form.monthlyRevenue === null ? undefined : form.monthlyRevenue}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, monthlyRevenue: e.target.value }));
                  clearError('monthlyRevenue');
                }}
              >
                <Space direction='vertical'>
                  {revenueRadioOptions.map((opt) => (
                    <Radio key={opt.value} value={opt.value}>
                      {opt.label}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
              {fieldError(formErrors, 'monthlyRevenue') !== null ? (
                <p className={styles.fieldError}>{fieldError(formErrors, 'monthlyRevenue')}</p>
              ) : null}
            </div>

            <div className={styles.buttons}>
              <Button
                type='primary'
                size='large'
                danger={surveyCreateErrored}
                disabled={isNextButtonDisabled || surveyPending || surveyCreated}
                loading={surveyPending}
                onClick={() => void handleSubmit()}
              >
                {t('page.registrationSurvey.actions.next')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
